import { useQuery } from "convex/react";
import {
	CornerDownLeftIcon,
	MessageSquareTextIcon,
	SearchIcon,
	TextSearchIcon,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import {
	Command,
	CommandDialog,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "@/components/ui/command";
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { api } from "../../../convex/_generated/api";

// Varied widths so the loading placeholder reads like a real result list.
const SKELETON_ROWS = ["w-2/5", "w-3/5", "w-1/2", "w-2/3", "w-1/3"];

function LoadingRows() {
	return (
		<div className="flex flex-col gap-1 p-1" aria-hidden>
			{SKELETON_ROWS.map((w) => (
				<div key={w} className="flex items-center gap-2 px-2 py-1.5">
					<Skeleton className="size-4 shrink-0 rounded-sm" />
					<Skeleton className={cn("h-3.5", w)} />
				</div>
			))}
		</div>
	);
}

/**
 * Global chat search palette (⌘K). Searches the viewer's chat threads by title
 * and message content via `globalSearch`; selecting a result navigates to the
 * thread (message results deep-link to the message via `?focus=`).
 */
export function GlobalSearchDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const [text, setText] = useState("");
	const navigate = useNavigate();
	const trimmed = text.trim();
	const results = useQuery(
		api.agent.queries.globalSearch,
		trimmed ? { text: trimmed } : "skip",
	);
	const threads = results?.threads ?? [];
	const messages = results?.messages ?? [];
	const loading = Boolean(trimmed) && results === undefined;
	const hasResults = threads.length > 0 || messages.length > 0;

	function close() {
		onOpenChange(false);
		setText("");
	}

	function go(clericalThreadId: string, messageId?: string) {
		const base = `/chat/${encodeURIComponent(clericalThreadId)}`;
		navigate(
			messageId ? `${base}?focus=${encodeURIComponent(messageId)}` : base,
		);
		close();
	}

	return (
		<CommandDialog
			open={open}
			onOpenChange={(next) => {
				if (!next) close();
				else onOpenChange(true);
			}}
			title="Search chats"
			description="Search your chat threads by title or message content"
			className="max-w-xl"
		>
			<Command shouldFilter={false}>
				<CommandInput
					value={text}
					onValueChange={setText}
					placeholder="Search threads and messages…"
				/>
				<CommandList className="max-h-[60vh] min-h-72">
					{!trimmed ? (
						<Empty className="py-12">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<SearchIcon />
								</EmptyMedia>
								<EmptyTitle>Search your chats</EmptyTitle>
								<EmptyDescription>
									Find a thread by title, or any message across your
									conversations.
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : loading ? (
						<LoadingRows />
					) : !hasResults ? (
						<Empty className="py-12">
							<EmptyHeader>
								<EmptyMedia variant="icon">
									<SearchIcon />
								</EmptyMedia>
								<EmptyTitle>No results</EmptyTitle>
								<EmptyDescription>
									Nothing matches “{trimmed}”. Try a different word.
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					) : (
						<>
							{threads.length > 0 ? (
								<CommandGroup heading={`Threads · ${threads.length}`}>
									{threads.map((t) => (
										<CommandItem
											key={t.clericalThreadId}
											value={`thread-${t.clericalThreadId}`}
											onSelect={() => go(t.clericalThreadId)}
										>
											<MessageSquareTextIcon className="text-muted-foreground" />
											<span className="truncate">{t.title}</span>
										</CommandItem>
									))}
								</CommandGroup>
							) : null}
							{threads.length > 0 && messages.length > 0 ? (
								<CommandSeparator />
							) : null}
							{messages.length > 0 ? (
								<CommandGroup heading={`Messages · ${messages.length}`}>
									{messages.map((m) => (
										<CommandItem
											key={m.messageId}
											value={`msg-${m.messageId}`}
											onSelect={() => go(m.clericalThreadId, m.messageId)}
											className="items-start"
										>
											<TextSearchIcon className="mt-0.5 text-muted-foreground" />
											<div className="flex min-w-0 flex-col gap-0.5">
												<span className="truncate text-xs text-muted-foreground">
													{m.threadTitle}
												</span>
												<span className="truncate">{m.snippet}</span>
											</div>
										</CommandItem>
									))}
								</CommandGroup>
							) : null}
						</>
					)}
				</CommandList>
				<div className="flex items-center gap-3 border-t px-3 py-2 text-[11px] text-muted-foreground">
					<span className="flex items-center gap-1.5">
						<Kbd>
							<CornerDownLeftIcon />
						</Kbd>
						open
					</span>
					<span className="flex items-center gap-1.5">
						<KbdGroup>
							<Kbd>↑</Kbd>
							<Kbd>↓</Kbd>
						</KbdGroup>
						navigate
					</span>
					<span className="flex items-center gap-1.5">
						<Kbd>esc</Kbd>
						close
					</span>
				</div>
			</Command>
		</CommandDialog>
	);
}
